// ── Profile Page (#/profile) — TPT81 ──
//
// Account-level (not project-scoped), same page-module shape as
// pages/security-settings.js: module state -> pure render*() string builders
// -> render(user) full innerHTML + bind(user) -> exported async page fn that
// resets state, verifySession()s, then renders. Editable here: avatar, name,
// headline, background, experience_years, via PATCH /auth/me
// (updateProfile(), auth.js). Email/password/2FA are NOT here — those live on
// #/account/security behind their own confirmation ceremony.
//
// Registered in index.js, not router.js (the task text's slip — router.js
// only defines addRoute/navigate/resolve and holds no app routes; every SPA
// route lives in index.js, same as C1496's own documented deviation).

import { checkAuth, getUser, verifySession, updateProfile } from '../auth.js';
import { showToast, esc } from '../ui.js';
import { navigate } from '../router.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { renderAvatarHtml } from '../utils.js';
import { EXPERIENCE_OPTIONS, profileErrorText } from '../lib/signup-form.js';
import { bindAvatarPicker } from '../lib/avatar-pick.js';

const AVATAR_PX = 96;

// The one tri-state control on this page — see buildProfilePatchBody()
// (lib/signup-form.js) for how each state maps onto the PATCH body:
//   unchanged -> avatar key omitted entirely
//   picked    -> avatar: pickedAvatar (new data: URL)
//   removed   -> avatar: null (clears the UPLOADED avatar only; a Google
//                avatar_url is untouched — see canRemoveAvatar() below)
let avatarState = 'unchanged';
let pickedAvatar = null;

// What the circle should show right now. Note renderAvatarHtml(user) with
// neither avatar_src nor avatar_url renders INITIALS, not the dashed "+"
// placeholder — passing null is what gets the placeholder, which is why the
// 'removed' branch below is not just `renderAvatarHtml(user, ...)`.
function previewHtml(user) {
  if (avatarState === 'picked') {
    return renderAvatarHtml({ avatar_url: pickedAvatar }, AVATAR_PX);
  }
  if (avatarState === 'removed') {
    // Removing only clears the uploaded avatar server-side — a Google
    // picture (avatar_url) still applies once the save lands.
    return user.avatar_url
      ? renderAvatarHtml({ avatar_url: user.avatar_url }, AVATAR_PX)
      : renderAvatarHtml(null, AVATAR_PX);
  }
  return user.avatar_src ? renderAvatarHtml(user, AVATAR_PX) : renderAvatarHtml(null, AVATAR_PX);
}

// "Remove photo" only when there is something an avatar:null would actually
// clear. A Google-only account (has_avatar false, avatar_url set) gets a
// hint instead of a button that would visibly do nothing.
function canRemoveAvatar(user) {
  return avatarState === 'picked' || (avatarState === 'unchanged' && !!user.has_avatar);
}

function renderProfileCard(user) {
  const expRaw = user.experience_years;
  // 0 ("Less than a year") is a real bucket — never seed with `|| ''`.
  const expValue = (expRaw === null || expRaw === undefined) ? '' : String(expRaw);
  const known = EXPERIENCE_OPTIONS.some((o) => String(o.value) === expValue);
  // experience_years is TINYINT 0-99 server-side; an out-of-bucket stored
  // value (e.g. written by some other API client) is kept as its own
  // selected option instead of silently resetting the user's data to
  // "Prefer not to say" the next time they save.
  const extraOption = (!known && expValue !== '')
    ? `<option value="${esc(expValue)}" selected>${esc(expValue)} years</option>` : '';
  const options = extraOption + EXPERIENCE_OPTIONS
    .map((o) => `<option value="${o.value}"${String(o.value) === expValue ? ' selected' : ''}>${esc(o.label)}</option>`)
    .join('');

  const showRemove = canRemoveAvatar(user);
  const showGoogleHint = !user.has_avatar && !!user.avatar_url;

  return `
  <div class="settings-section">
    <div class="card">
      <form id="profile-form" style="max-width:520px;">
        <input type="file" id="profile-avatar-input" accept="image/*" hidden>
        <div class="form-group" style="text-align:center;">
          <span id="profile-avatar-preview" class="avatar-drop-zone" style="display:inline-block;cursor:pointer;" title="Change photo">${previewHtml(user)}</span>
          <div>
            <button type="button" class="btn btn-ghost btn-sm" id="profile-avatar-remove" style="margin-top:0.5rem;${showRemove ? '' : 'display:none;'}">Remove photo</button>
          </div>
          ${showGoogleHint ? '<p class="text-muted">Using your Google profile picture — upload one to replace it.</p>' : ''}
        </div>
        <div class="form-group">
          <label class="form-label" for="profile-email">Email</label>
          <input class="form-input" id="profile-email" type="email" value="${esc(user.email || '')}" readonly>
          <p class="text-muted">Email, password, and two-factor authentication are managed on the <a href="#/account/security">Security</a> page.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="profile-name">Name</label>
          <input class="form-input" id="profile-name" maxlength="255" autocomplete="name" required value="${esc(user.name || '')}">
        </div>
        <div class="form-group">
          <label class="form-label" for="profile-headline">Headline</label>
          <input class="form-input" id="profile-headline" maxlength="160" placeholder="e.g. Senior backend engineer" value="${esc(user.headline || '')}">
        </div>
        <div class="form-group">
          <label class="form-label" for="profile-background">Background</label>
          <textarea class="form-textarea" id="profile-background" maxlength="4000">${esc(user.background || '')}</textarea>
        </div>
        <div class="form-group">
          <label class="form-label" for="profile-experience">Experience</label>
          <select class="form-select" id="profile-experience">${options}</select>
        </div>
        <div class="login-error" id="profile-error"></div>
        <div class="flex gap-1">
          <button type="submit" class="btn btn-primary" id="profile-save">Save</button>
          <button type="button" class="btn btn-ghost" id="profile-cancel" title="Discard changes">Cancel</button>
        </div>
      </form>
    </div>
  </div>`;
}

function render(user) {
  getContentEl().innerHTML = `<div class="page-header"><h2>Profile</h2></div>${renderProfileCard(user)}`;
  bind(user);
}

function bind(user) {
  const content = getContentEl();
  const errEl = content.querySelector('#profile-error');
  const preview = content.querySelector('#profile-avatar-preview');

  bindAvatarPicker({
    preview,
    input: content.querySelector('#profile-avatar-input'),
    removeBtn: content.querySelector('#profile-avatar-remove'),
    form: content.querySelector('#profile-form'),
    onPick: (dataUrl) => {
      avatarState = 'picked';
      pickedAvatar = dataUrl;
      preview.innerHTML = previewHtml(user);
    },
    onRemove: () => {
      avatarState = 'removed';
      pickedAvatar = null;
      preview.innerHTML = previewHtml(user);
    },
    onError: (msg) => { errEl.textContent = msg; },
  });

  // Discard edits and repaint from the cached user. Not a navigation — this
  // is a standalone page with no obvious "back".
  content.querySelector('#profile-cancel').addEventListener('click', () => {
    avatarState = 'unchanged';
    pickedAvatar = null;
    render(getUser());
  });

  content.querySelector('#profile-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = content.querySelector('#profile-save');
    errEl.textContent = '';

    const name = content.querySelector('#profile-name').value;
    if (!name.trim()) {
      // Same copy the server 400s with — pre-checked so a blank name never
      // makes a pointless round-trip.
      errEl.textContent = 'Name is required';
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Saving...';
    try {
      const updated = await updateProfile({
        name,
        headline: content.querySelector('#profile-headline').value,
        background: content.querySelector('#profile-background').value,
        experience_years: content.querySelector('#profile-experience').value,
        avatar: avatarState === 'picked' ? pickedAvatar : avatarState === 'removed' ? null : undefined,
      });
      avatarState = 'unchanged';
      pickedAvatar = null;
      showToast('Profile saved', 'success');
      render(updated || getUser());
    } catch (err) {
      if (err.status === 401) {
        // _silent means this is ours to handle: the session died mid-edit,
        // nothing on this page can fix that.
        showToast('Your session expired — sign in again', 'error');
        navigate('/login');
        return;
      }
      // Inline, form NOT re-rendered — a re-render here would wipe every
      // typed field (same rule the signup wizard follows for its own
      // 'complete' inline errors).
      errEl.textContent = profileErrorText(err);
      btn.disabled = false;
      btn.textContent = 'Save';
    }
  });
}

export async function profilePage() {
  if (!(await checkAuth())) return;

  initLayout(getUser());
  updateLayoutContext({ projectId: null, projectName: null, activeView: null, viewLabel: 'Profile' });

  avatarState = 'unchanged';
  pickedAvatar = null;
  // Cached currentUser may predate an edit made in another tab/session —
  // this page shows every field it's about to overwrite, so re-read first.
  await verifySession();
  render(getUser());
}
